import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Animated,
  AppState,
  BackHandler,
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Swipeable } from 'react-native-gesture-handler';

import { CHAT_AQUA, contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/lib/supabase';
import { getAgreement, fetchProfilesByIds } from '@/lib/agreements';
import { fetchAgreementParties } from '@/lib/multiParty';
import {
  DELETED_SENDER_ID, fetchAgreementMessageReads, fetchMessages, fetchReactions, markAgreementMessagesRead,
  sendMessage, toggleReaction, MESSAGE_REACTION_EMOJIS, type AgreementMessage, type DbAgreementMessage,
  type MessageReaction, type MessageReactionEmoji,
} from '@/lib/agreementMessages';
import type { Profile } from '@/lib/database.types';
import { chatDisplayName, chatParticipantName } from '@/lib/format';
import { autoTransliterateOnWordBoundary } from '@/lib/greeklish';
import { getErrorMessage } from '@/lib/functions';
import { playPop, playTap } from '@/lib/sounds';
import {
  blockUser,
  fetchAgreementPartyIds,
  fetchMyBlockRelations,
  reportChatMessage,
  unblockUser,
  type ChatReportReason,
} from '@/lib/chatModeration';
import { ErrorText } from '@/components/ErrorText';

const REPORT_REASONS: ChatReportReason[] = ['spam', 'offensive', 'scam', 'harassment', 'other'];

const TYPING_BROADCAST_THROTTLE_MS = 2000;
const TYPING_EXPIRE_MS = 4000;
// Ίδια λίστα με το RLS policy του agreement_messages_insert — καμία νέα
// αποστολή μηνύματος μετά από αυτές τις καταστάσεις (ξεκινάει το retention window).
const CLOSED_STATUSES = ['draft', 'completed', 'cancelled', 'terminated', 'cancelled_mutual', 'cancelled_unilateral', 'withdrawn', 'cancelled_unsigned_timeout'];

export default function AgreementMessagesScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { profile } = useProfile();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [loading, setLoading] = useState(true);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [messages, setMessages] = useState<AgreementMessage[]>([]);
  const [senders, setSenders] = useState<Record<string, Profile>>({});
  const [agreementTitle, setAgreementTitle] = useState('');
  const [isClosed, setIsClosed] = useState(false);
  const [isDisputed, setIsDisputed] = useState(false);
  // Σε multi-party συμφωνία, όποιος δεν έχει υπογράψει ακόμα δεν είναι
  // ενεργό μέρος (RLS απαιτεί has_signed=true) — πρέπει να το ξέρουμε ΠΡΙΝ
  // την απόπειρα αποστολής, ώστε να δείξουμε τον σωστό λόγο αντί το γενικό
  // "η συμφωνία έχει ολοκληρωθεί" (που εμφανιζόταν επειδή parse-άραμε το
  // ίδιο raw "row-level security policy" σφάλμα και για τις δύο περιπτώσεις).
  const [needsSignature, setNeedsSignature] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});
  const [replyTo, setReplyTo] = useState<AgreementMessage | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);
  // Γραμμή "Μη αναγνωσμένα" — υπολογίζεται ΜΙΑ φορά κατά το πρώτο άνοιγμα (πριν
  // σημειωθούν τα μηνύματα ως διαβασμένα), δείχνει από πού να ξεκινήσει να
  // διαβάζει ο χρήστης. Χάνεται μόλις φτάσει scroll στο κάτω μέρος.
  const [unreadDividerMessageId, setUnreadDividerMessageId] = useState<string | null>(null);
  const [unreadDividerVisible, setUnreadDividerVisible] = useState(false);
  const initialUnreadComputedRef = useRef(false);
  const initialScrollDoneRef = useRef(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastTypingSentRef = useRef(0);
  const typingTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const swipeablesRef = useRef<Record<string, Swipeable | null>>({});
  const [reconnectTick, setReconnectTick] = useState(0);

  const [partyIds, setPartyIds] = useState<string[]>([]);
  // user_id -> τελευταία στιγμή που το διάβασε — για την ένδειξη "διαβάστηκε"
  // δίπλα στα δικά μου μηνύματα.
  const [reads, setReads] = useState<Record<string, string>>({});
  // Η δική μου φούσκα είναι ΠΑΝΤΑ aqua (CHAT_AQUA) — σταθερό, ίδιο σε κάθε
  // chat. Κάθε άλλος συμμετέχων παίρνει το δικό του σταθερό χρώμα (ακόμα και
  // σε συνομιλία 2 ατόμων), ώστε να ξεχωρίζουν όλοι με μια ματιά. Ταξινομημένο
  // ώστε ΟΛΟΙ οι συμμετέχοντες να βλέπουν τα ΙΔΙΑ χρώματα για τον καθένα.
  const OTHER_PARTY_COLORS = ['#60A5FA', '#34D399', '#F472B6', '#A78BFA', '#FB923C', '#22D3EE', '#FBBF24', '#F87171'];
  const senderColors = useMemo(() => {
    const others = partyIds.filter((id) => id !== user?.id).sort();
    const map: Record<string, string> = {};
    others.forEach((id, i) => { map[id] = OTHER_PARTY_COLORS[i % OTHER_PARTY_COLORS.length]; });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyIds, user?.id]);
  const [blockRelations, setBlockRelations] = useState<{ blocker_id: string; blocked_id: string }[]>([]);
  const [reactions, setReactions] = useState<MessageReaction[]>([]);
  const [actionSheetFor, setActionSheetFor] = useState<AgreementMessage | null>(null);
  const [reportTarget, setReportTarget] = useState<AgreementMessage | null>(null);
  const [reportReason, setReportReason] = useState<ChatReportReason>('spam');
  const [reportNote, setReportNote] = useState('');
  const [reporting, setReporting] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);

  // Το πρώτο άτομο της συμφωνίας που έχω μπλοκάρει (ή που με έχει μπλοκάρει) —
  // αρκεί για το UI, ακόμα κι αν θεωρητικά υπάρχουν παραπάνω σε multi-party chat.
  const blockedByMeId = useMemo(
    () => blockRelations.find((r) => r.blocker_id === user?.id && partyIds.includes(r.blocked_id))?.blocked_id ?? null,
    [blockRelations, partyIds, user?.id],
  );
  const blockedMeId = useMemo(
    () => blockRelations.find((r) => r.blocked_id === user?.id && partyIds.includes(r.blocker_id))?.blocker_id ?? null,
    [blockRelations, partyIds, user?.id],
  );
  const isBlocked = !!blockedByMeId || !!blockedMeId;

  async function refreshBlockStatus() {
    if (!id) return;
    try {
      const [ids, relations] = await Promise.all([fetchAgreementPartyIds(id), fetchMyBlockRelations()]);
      setPartyIds(ids);
      setBlockRelations(relations);
      const missingIds = ids.filter((pid) => !senders[pid]);
      if (missingIds.length > 0) {
        const map = await fetchProfilesByIds(missingIds);
        setSenders((prev) => ({ ...prev, ...map }));
      }
    } catch { /* μη κρίσιμο — απλά δεν θα φανεί η ένδειξη block */ }
  }

  const messagesById = useMemo(() => {
    const map: Record<string, AgreementMessage> = {};
    for (const m of messages) map[m.id] = m;
    return map;
  }, [messages]);

  const unreadDividerIndex = useMemo(
    () => (unreadDividerMessageId ? messages.findIndex((m) => m.id === unreadDividerMessageId) : -1),
    [messages, unreadDividerMessageId],
  );

  // message_id -> { emoji -> count }, και message_id -> το δικό μου emoji (αν υπάρχει)
  const reactionsByMessage = useMemo(() => {
    const counts: Record<string, Record<string, number>> = {};
    const mine: Record<string, MessageReaction> = {};
    for (const r of reactions) {
      counts[r.message_id] = counts[r.message_id] ?? {};
      counts[r.message_id][r.emoji] = (counts[r.message_id][r.emoji] ?? 0) + 1;
      if (r.user_id === user?.id) mine[r.message_id] = r;
    }
    return { counts, mine };
  }, [reactions, user?.id]);

  async function onPickReaction(messageId: string, emoji: MessageReactionEmoji) {
    if (!id || !user) return;
    setActionSheetFor(null);
    const existing = reactionsByMessage.mine[messageId];
    // Optimistic update — μη περιμένεις το realtime echo.
    setReactions((prev) => {
      const withoutMine = prev.filter((r) => !(r.message_id === messageId && r.user_id === user.id));
      if (existing && existing.emoji === emoji) return withoutMine;
      return [...withoutMine, { id: existing?.id ?? `temp-${messageId}-${user.id}`, message_id: messageId, user_id: user.id, emoji }];
    });
    try {
      await toggleReaction(id, messageId, user.id, emoji, existing);
    } catch {
      void load();
    }
  }

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [agreement, msgs, parties, readRows, allPartyIds, reactionRows] = await Promise.all([
        getAgreement(id), fetchMessages(id), fetchAgreementParties(id).catch(() => []), fetchAgreementMessageReads(id).catch(() => ({})),
        fetchAgreementPartyIds(id).catch(() => []), fetchReactions(id).catch(() => []),
      ]);
      setReactions(reactionRows);
      setReads(readRows);
      setPartyIds(allPartyIds);
      setAgreementTitle(agreement?.title ?? '');
      setIsClosed(!!agreement?.status && CLOSED_STATUSES.includes(agreement.status));
      setIsDisputed(agreement?.status === 'disputed');
      const myParty = parties.find((p) => p.user_id === user?.id);
      setNeedsSignature(!!myParty && !myParty.has_signed);
      setMessages(msgs);
      if (!initialUnreadComputedRef.current) {
        initialUnreadComputedRef.current = true;
        const myPrevRead = (readRows as Record<string, string>)[user?.id ?? ''];
        if (myPrevRead) {
          const cutoff = new Date(myPrevRead).getTime();
          const firstUnread = msgs.find((m) => m.sender_id !== user?.id && new Date(m.created_at).getTime() > cutoff);
          if (firstUnread) {
            setUnreadDividerMessageId(firstUnread.id);
            setUnreadDividerVisible(true);
          }
        }
      }
      // Και οι αποστολείς μηνυμάτων ΚΑΙ όλοι οι συμμετέχοντες (ακόμα κι αν δεν
      // έχουν γράψει ακόμα τίποτα) — χρειάζονται για τη γραμμή "Συμμετέχουν οι...".
      const senderIds = [...new Set([...msgs.map((m) => m.sender_id), ...allPartyIds])];
      if (senderIds.length > 0) setSenders(await fetchProfilesByIds(senderIds));
      await AsyncStorage.setItem(`agreement_messages_seen_${id}`, new Date().toISOString());
      void markAgreementMessagesRead(id).catch(() => {});
      void refreshBlockStatus();
    } catch {
      setError(t('agreementDetail.messagesLoadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t, user?.id]);

  // Το ενημερωτικό μήνυμα (GDPR notice) κλείνει μόνο για την τρέχουσα επίσκεψη
  // στο chat — δεν αποθηκεύεται πουθενά, οπότε ξαναφαίνεται κάθε φορά που ο
  // χρήστης ξαναμπαίνει στη συνομιλία.
  function dismissNotice() {
    setNoticeDismissed(true);
  }

  // background — όπως κάθε IM app, μόλις επιστρέψουμε στο προσκήνιο ξαναφέρνουμε
  // ό,τι χάθηκε (load) ΚΑΙ ξανασυνδέουμε το channel (μέσω reconnectTick, που
  // ξαναρχίζει το παρακάτω useFocusEffect από την αρχή).
  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        void load();
        setReconnectTick((t) => t + 1);
      }
    });
    return () => sub.remove();
  }, [load]);

  useFocusEffect(
    useCallback(() => {
      void load();
      if (!id) return;
      const channel = supabase
        .channel(`agreement-messages-${id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'agreement_messages',
          filter: `agreement_id=eq.${id}`,
        }, (payload) => {
          const raw = payload.new as DbAgreementMessage;
          const row: AgreementMessage = { ...raw, sender_id: raw.sender_id ?? DELETED_SENDER_ID };
          setMessages((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, row]));
          if (row.sender_id !== user?.id) playPop();
          requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
          if (!senders[row.sender_id]) {
            void fetchProfilesByIds([row.sender_id]).then((map) => setSenders((prev) => ({ ...prev, ...map })));
          }
          void AsyncStorage.setItem(`agreement_messages_seen_${id}`, new Date().toISOString());
          void markAgreementMessagesRead(id).catch(() => {});
          // Μόλις έφτασε πραγματικό μήνυμα από αυτόν τον χρήστη, σταμάτα να δείχνεις "πληκτρολογεί".
          setTypingUsers((prev) => {
            if (!(row.sender_id in prev)) return prev;
            const next = { ...prev };
            delete next[row.sender_id];
            return next;
          });
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'agreement_message_reads',
          filter: `agreement_id=eq.${id}`,
        }, (payload) => {
          const row = payload.new as { user_id: string; last_read_at: string } | undefined;
          if (!row) return;
          setReads((prev) => ({ ...prev, [row.user_id]: row.last_read_at }));
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'agreement_message_reactions',
          filter: `agreement_id=eq.${id}`,
        }, (payload) => {
          const row = payload.new as MessageReaction;
          setReactions((prev) => (prev.some((r) => r.id === row.id) ? prev : [...prev, row]));
        })
        .on('postgres_changes', {
          event: 'DELETE',
          schema: 'public',
          table: 'agreement_message_reactions',
          filter: `agreement_id=eq.${id}`,
        }, (payload) => {
          const row = payload.old as { id: string };
          setReactions((prev) => prev.filter((r) => r.id !== row.id));
        })
        .on('broadcast', { event: 'typing' }, (payload) => {
          const { userId, name } = payload.payload as { userId: string; name: string };
          if (userId === user?.id) return;
          setTypingUsers((prev) => ({ ...prev, [userId]: name }));
          if (typingTimersRef.current[userId]) clearTimeout(typingTimersRef.current[userId]);
          typingTimersRef.current[userId] = setTimeout(() => {
            setTypingUsers((prev) => {
              const next = { ...prev };
              delete next[userId];
              return next;
            });
          }, TYPING_EXPIRE_MS);
        })
        .subscribe();
      channelRef.current = channel;
      return () => {
        void supabase.removeChannel(channel);
        channelRef.current = null;
        Object.values(typingTimersRef.current).forEach(clearTimeout);
        typingTimersRef.current = {};
        setTypingUsers({});
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, load, reconnectTick]),
  );

  function onChangeDraft(rawText: string) {
    const text = autoTransliterateOnWordBoundary(rawText);
    setDraft(text);
    const now = Date.now();
    if (channelRef.current && user && now - lastTypingSentRef.current > TYPING_BROADCAST_THROTTLE_MS) {
      lastTypingSentRef.current = now;
      const name = profile?.full_name?.trim() || t('common.user');
      void channelRef.current.send({ type: 'broadcast', event: 'typing', payload: { userId: user.id, name } });
    }
  }

  const listRef = useRef<FlatList<AgreementMessage>>(null);

  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [] as number[];
    return messages.reduce<number[]>((acc, m, i) => {
      if (m.body.toLowerCase().includes(q)) acc.push(i);
      return acc;
    }, []);
  }, [messages, searchQuery]);

  const clampedMatchIndex = searchMatches.length > 0
    ? Math.min(matchIndex, searchMatches.length - 1)
    : 0;

  function scrollToMessageIndex(index: number) {
    listRef.current?.scrollToIndex({ index, animated: true, viewPosition: 0.4 });
  }

  function goToMatch(delta: number) {
    if (searchMatches.length === 0) return;
    const next = (clampedMatchIndex + delta + searchMatches.length) % searchMatches.length;
    setMatchIndex(next);
    scrollToMessageIndex(searchMatches[next]);
  }

  function onChangeSearch(text: string) {
    setSearchQuery(text);
    setMatchIndex(0);
  }

  useEffect(() => {
    if (searchMatches.length > 0) scrollToMessageIndex(searchMatches[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatches.length > 0 ? searchMatches[0] : -1, searchQuery]);

  function onToggleSearch() {
    setSearchOpen((v) => {
      const next = !v;
      if (!next) { setSearchQuery(''); setMatchIndex(0); }
      return next;
    });
  }

  function highlightedText(text: string, query: string, highlightStyle: object, textStyle: object) {
    const q = query.trim();
    if (!q) return <Text style={textStyle}>{text}</Text>;
    const lower = text.toLowerCase();
    const qLower = q.toLowerCase();
    const parts: { text: string; match: boolean }[] = [];
    let cursor = 0;
    let idx = lower.indexOf(qLower, cursor);
    while (idx !== -1) {
      if (idx > cursor) parts.push({ text: text.slice(cursor, idx), match: false });
      parts.push({ text: text.slice(idx, idx + q.length), match: true });
      cursor = idx + q.length;
      idx = lower.indexOf(qLower, cursor);
    }
    if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
    return (
      <Text style={textStyle}>
        {parts.map((p, i) => (p.match
          ? <Text key={i} style={highlightStyle}>{p.text}</Text>
          : <Text key={i}>{p.text}</Text>
        ))}
      </Text>
    );
  }

  async function onSend() {
    const body = draft.trim();
    if (!body || !id || !user || sending || isClosed || isDisputed || isBlocked || needsSignature) return;
    setSending(true);
    setError(null);
    setDraft('');
    const replyToId = replyTo?.id ?? null;
    setReplyTo(null);
    try {
      const sent = await sendMessage(id, user.id, body, replyToId);
      // Μην περιμένεις το realtime postgres_changes echo (μπορεί να καθυστερήσει
      // ή να μην έχει προλάβει να συνδεθεί ο listener) — δείξε το μήνυμα αμέσως.
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
      playTap();
      requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
    } catch (e) {
      const raw = getErrorMessage(e, t('agreementDetail.messagesSendError'));
      setError(raw.includes('row-level security policy')
        ? (needsSignature ? t('agreementDetail.messagesNeedsSignature') : t('agreementDetail.messagesClosedAgreement'))
        : raw);
      setDraft(body);
      if (replyToId) setReplyTo(messagesById[replyToId] ?? null);
    } finally {
      setSending(false);
    }
  }

  // Πάντα πίσω στην οθόνη της συμφωνίας — όχι router.back(), γιατί αν ήρθαμε
  // εδώ από notification tap, το chat μπορεί να είναι η πρώτη οθόνη στο stack
  // (χωρίς να προηγείται η οθόνη συμφωνίας) και το back() θα πήγαινε αλλού.
  function goBackToAgreement() {
    // Αν υπάρχει ήδη η οθόνη συμφωνίας στο navigation stack (π.χ. ήρθαμε εδώ
    // πατώντας "Άνοιγμα chat συμφωνίας" μέσα από τον οδηγό μιας διαφωνίας),
    // κάνε απλό back() — ΔΙΑΤΗΡΕΙ την κατάσταση εκείνου του instance (π.χ. σε
    // ποιο βήμα του wizard ήταν, τι evidence είχε ήδη επισυνάψει). Το
    // replace() θα το κατέστρεφε εντελώς και θα ξεκινούσε από την αρχή.
    // Μόνο αν δεν υπάρχει τίποτα να επιστρέψουμε (π.χ. ήρθαμε από
    // notification, χωρίς προηγούμενη οθόνη) πάμε με replace σε νέο instance.
    if (id && router.canGoBack()) { router.back(); return; }
    if (!id) { router.back(); return; }
    router.replace({ pathname: '/(app)/agreements/[id]', params: { id } } as never);
  }

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      goBackToAgreement();
      return true;
    });
    return () => sub.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function onSwipeReply(item: AgreementMessage) {
    swipeablesRef.current[item.id]?.close();
    setReplyTo(item);
  }

  function replyPreviewLabel(m: AgreementMessage): string {
    return m.sender_id === user?.id
      ? t('agreementDetail.messagesYou')
      : senders[m.sender_id]?.full_name || t('common.user');
  }

  function nameFor(userId: string): string {
    return senders[userId]?.full_name || t('common.user');
  }

  function onLongPressMessage(item: AgreementMessage) {
    setActionSheetFor(item);
  }

  function onOpenReportFromSheet() {
    if (!actionSheetFor) return;
    setReportTarget(actionSheetFor);
    setReportReason('spam');
    setReportNote('');
    setActionSheetFor(null);
  }

  async function onSubmitReport() {
    if (!reportTarget || !id) return;
    setReporting(true);
    try {
      await reportChatMessage({
        agreementId: id,
        reportedUserId: reportTarget.sender_id,
        reason: reportReason,
        messageId: reportTarget.id,
        note: reportNote,
      });
      setReportTarget(null);
      appAlert(t('agreementDetail.reportSentTitle'), t('agreementDetail.reportSentBody'));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('agreementDetail.reportError')));
    } finally {
      setReporting(false);
    }
  }

  function onBlockFromSheet() {
    if (!actionSheetFor) return;
    const targetId = actionSheetFor.sender_id;
    const targetName = nameFor(targetId);
    setActionSheetFor(null);
    appAlert(
      t('agreementDetail.blockConfirmTitle', { name: targetName }),
      t('agreementDetail.blockConfirmBody', { name: targetName }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('agreementDetail.blockAction'),
          style: 'destructive',
          onPress: async () => {
            setBlockBusy(true);
            try {
              await blockUser(targetId);
              await refreshBlockStatus();
            } catch (e) {
              appAlert(t('common.error'), getErrorMessage(e, t('agreementDetail.blockError')));
            } finally {
              setBlockBusy(false);
            }
          },
        },
      ],
    );
  }

  async function onUnblock() {
    if (!blockedByMeId) return;
    setBlockBusy(true);
    try {
      await unblockUser(blockedByMeId);
      await refreshBlockStatus();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('agreementDetail.blockError')));
    } finally {
      setBlockBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top : 0}
    >
      <View style={[styles.header, { paddingTop: insets.top + spacing.sm }]}>
        <Pressable onPress={goBackToAgreement} hitSlop={12} accessibilityRole="button">
          <Ionicons name="chevron-back" size={24} color={colors.text} />
        </Pressable>
        <Text style={styles.headerTitle} numberOfLines={1}>{agreementTitle || t('agreementDetail.messagesTitle')}</Text>
        <Pressable onPress={onToggleSearch} hitSlop={12} accessibilityRole="button" accessibilityLabel={t('agreementDetail.messagesSearchLabel')}>
          <Ionicons name={searchOpen ? 'close' : 'search'} size={22} color={colors.text} />
        </Pressable>
      </View>

      {partyIds.length > 0 && (
        <Text style={styles.participantsLine} numberOfLines={1}>
          {t('agreementDetail.messagesParticipantsPrefix')}{' '}
          {partyIds.map((pid, i) => (
            <Text key={pid}>
              <Text style={{ color: pid === user?.id ? CHAT_AQUA : senderColors[pid], fontWeight: '700' }}>
                {chatParticipantName(senders[pid]?.full_name ?? null, senders[pid]?.nickname)}
              </Text>
              {i < partyIds.length - 1 ? ', ' : ''}
            </Text>
          ))}
        </Text>
      )}

      {searchOpen && (
        <View style={styles.searchBar}>
          <Ionicons name="search" size={16} color={colors.textMuted} />
          <TextInput
            value={searchQuery}
            onChangeText={onChangeSearch}
            placeholder={t('agreementDetail.messagesSearchPlaceholder')}
            placeholderTextColor={colors.textMuted}
            style={styles.searchInput}
            autoFocus
          />
          {!!searchQuery.trim() && (
            <Text style={styles.searchCount}>
              {searchMatches.length > 0 ? `${clampedMatchIndex + 1}/${searchMatches.length}` : '0/0'}
            </Text>
          )}
          <Pressable onPress={() => goToMatch(-1)} disabled={searchMatches.length === 0} hitSlop={8} accessibilityRole="button">
            <Ionicons name="chevron-up" size={20} color={searchMatches.length > 0 ? colors.text : colors.textMuted} />
          </Pressable>
          <Pressable onPress={() => goToMatch(1)} disabled={searchMatches.length === 0} hitSlop={8} accessibilityRole="button">
            <Ionicons name="chevron-down" size={20} color={searchMatches.length > 0 ? colors.text : colors.textMuted} />
          </Pressable>
        </View>
      )}

      {!noticeDismissed && (
        <View style={styles.noticeBanner}>
          <View style={styles.noticeIconRow}>
            <Ionicons name="shield-checkmark-outline" size={14} color={colors.textMuted} />
            <Text style={styles.noticeText}>
              {t('agreementDetail.messagesGdprNotice', {
                defaultValue: 'Τα μηνύματα εδώ αποθηκεύονται και μπορεί να χρησιμοποιηθούν ως αποδεικτικό σε τυχόν διαφωνία για αυτή τη συμφωνία. Προτείνεται να συνεννοείστε για ό,τι αφορά τη συμφωνία μέσα από αυτό το chat, ώστε να έχετε αποδείξεις αν χρειαστεί να διαφωνήσετε.',
              })}
            </Text>
          </View>
          <Pressable onPress={dismissNotice} hitSlop={8} accessibilityRole="button" style={styles.noticeDismissBtn}>
            <Text style={styles.noticeDismissText}>{t('agreementDetail.messagesGdprAck', { defaultValue: 'Ενημερώθηκα' })}</Text>
          </Pressable>
        </View>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={CHAT_AQUA} />
        </View>
      ) : (
        <FlatList
          ref={listRef}
          data={messages}
          keyExtractor={(m) => m.id}
          contentContainerStyle={styles.listContent}
          onContentSizeChange={() => {
            if (searchOpen) return;
            if (!initialScrollDoneRef.current) {
              initialScrollDoneRef.current = true;
              if (unreadDividerIndex >= 0) {
                listRef.current?.scrollToIndex({ index: unreadDividerIndex, animated: false, viewPosition: 0 });
                return;
              }
            }
            listRef.current?.scrollToEnd({ animated: false });
          }}
          onScrollToIndexFailed={(info) => {
            setTimeout(() => {
              listRef.current?.scrollToIndex({ index: info.index, animated: true, viewPosition: 0.4 });
            }, 100);
          }}
          onScroll={(e) => {
            if (!unreadDividerVisible) return;
            const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;
            if (contentOffset.y + layoutMeasurement.height >= contentSize.height - 40) {
              setUnreadDividerVisible(false);
            }
          }}
          scrollEventThrottle={200}
          ListEmptyComponent={
            <View style={styles.center}>
              <Ionicons name="chatbubbles-outline" size={40} color={colors.border} />
              <Text style={styles.emptyText}>{t('agreementDetail.messagesEmpty')}</Text>
            </View>
          }
          renderItem={({ item, index }) => {
            const mine = item.sender_id === user?.id;
            const senderProfile = senders[item.sender_id] as (Profile & { portfolio_enabled?: boolean; portfolio_public?: boolean; business_name?: string | null; show_business_name?: boolean }) | undefined;
            const senderName = mine
              ? t('agreementDetail.messagesYou')
              : chatDisplayName(senderProfile?.full_name ?? null, senderProfile?.nickname) || t('common.user');
            // Ενεργός δημόσιος επαγγελματικός λογαριασμός με δηλωμένη επιχείρηση —
            // δείχνουμε "από [επιχείρηση]" κάτω από το όνομα, ώστε ο συνομιλητής
            // να ξέρει ότι μιλάει με κάποιον υπό επαγγελματική ιδιότητα.
            const businessLine = !mine && senderProfile?.portfolio_enabled && senderProfile?.portfolio_public
              && senderProfile?.show_business_name && senderProfile.business_name?.trim()
              ? t('agreementDetail.messagesFromBusiness', { name: senderProfile.business_name.trim() })
              : null;
            const quoted = item.reply_to_id ? messagesById[item.reply_to_id] : null;
            const isActiveMatch = searchOpen && searchMatches.length > 0 && searchMatches[clampedMatchIndex] === index;
            const otherColor = !mine ? senderColors[item.sender_id] : undefined;
            // "Διαβάστηκε" — μόνο για δικά μου μηνύματα: ονόματα όσων άλλων
            // συμβαλλόμενων έχουν last_read_at >= τη στιγμή αποστολής.
            const readerNames = mine
              ? partyIds
                  .filter((pid) => pid !== user?.id)
                  .filter((pid) => { const lr = reads[pid]; return !!lr && new Date(lr) >= new Date(item.created_at); })
                  .map((pid) => chatDisplayName(senders[pid]?.full_name ?? null, senders[pid]?.nickname))
              : [];
            const isRead = readerNames.length > 0;
            return (
              <>
                {unreadDividerVisible && item.id === unreadDividerMessageId && (
                  <View style={styles.unreadDivider}>
                    <View style={styles.unreadDividerLine} />
                    <Text style={styles.unreadDividerText}>{t('agreementDetail.messagesUnreadDivider')}</Text>
                    <View style={styles.unreadDividerLine} />
                  </View>
                )}
                <Swipeable
                ref={(ref) => { swipeablesRef.current[item.id] = ref; }}
                renderLeftActions={(_progress, dragX) => {
                  const opacity = dragX.interpolate({ inputRange: [0, 50], outputRange: [0, 1], extrapolate: 'clamp' });
                  return (
                    <Animated.View style={[styles.swipeReplyIcon, { opacity }]}>
                      <Ionicons name="arrow-undo" size={18} color={CHAT_AQUA} />
                    </Animated.View>
                  );
                }}
                leftThreshold={48}
                friction={2}
                overshootLeft={false}
                onSwipeableOpen={() => onSwipeReply(item)}
              >
                <Pressable
                  style={[styles.bubbleRow, mine && styles.bubbleRowMine]}
                  onLongPress={() => onLongPressMessage(item)}
                  delayLongPress={350}
                >
                  {!mine && (
                    <Pressable
                      style={styles.bubbleMenuBtn}
                      onPress={() => onLongPressMessage(item)}
                      hitSlop={10}
                      accessibilityRole="button"
                      accessibilityLabel={t('agreementDetail.messageOptionsAction')}
                    >
                      <Ionicons name="ellipsis-vertical" size={16} color={colors.textMuted} />
                    </Pressable>
                  )}
                  <View style={styles.bubbleColumn}>
                    <View style={[
                      styles.bubble,
                      mine ? styles.bubbleMine : styles.bubbleOther,
                      !!otherColor && { borderColor: otherColor, borderWidth: 1.5 },
                      isActiveMatch && styles.bubbleActiveMatch,
                    ]}>
                      <Text style={[styles.bubbleSender, mine && styles.bubbleSenderMine, !!otherColor && { color: otherColor }]}>{senderName}</Text>
                      {!!businessLine && <Text style={styles.bubbleBusinessLine}>{businessLine}</Text>}
                      {!!quoted && (
                        <View style={[styles.quoteBox, mine && styles.quoteBoxMine]}>
                          <Text style={[styles.quoteSender, mine && styles.quoteSenderMine]} numberOfLines={1}>
                            {replyPreviewLabel(quoted)}
                          </Text>
                          <Text style={[styles.quoteText, mine && styles.quoteTextMine]} numberOfLines={2}>
                            {quoted.body}
                          </Text>
                        </View>
                      )}
                      {highlightedText(
                        item.body,
                        searchOpen ? searchQuery : '',
                        styles.bubbleHighlight,
                        [styles.bubbleText, mine && styles.bubbleTextMine],
                      )}
                      <View style={styles.bubbleMetaRow}>
                        <Text style={[styles.bubbleTime, mine && styles.bubbleTimeMine]}>
                          {new Date(item.created_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                        {mine && (
                          isRead ? (
                            <Text style={styles.bubbleReadEmoji}>👀✔️</Text>
                          ) : (
                            <Ionicons name="checkmark" size={14} color="rgba(255,255,255,0.6)" style={styles.bubbleReadIcon} />
                          )
                        )}
                      </View>
                      {isRead && (
                        <Text style={styles.bubbleReadByText}>
                          {t('agreementDetail.messagesReadBy', { names: readerNames.join(', ') })}
                        </Text>
                      )}
                    </View>
                    {!!reactionsByMessage.counts[item.id] && (
                      <Pressable
                        style={[styles.reactionsBadge, mine && styles.reactionsBadgeMine]}
                        onPress={() => onLongPressMessage(item)}
                        hitSlop={6}
                        accessibilityRole="button"
                      >
                        {Object.entries(reactionsByMessage.counts[item.id]).map(([emoji, count]) => (
                          <Text key={emoji} style={styles.reactionsBadgeText}>
                            {emoji}{count > 1 ? count : ''}
                          </Text>
                        ))}
                      </Pressable>
                    )}
                  </View>
                </Pressable>
                </Swipeable>
              </>
            );
          }}
        />
      )}

      {Object.keys(typingUsers).length > 0 && (
        <View style={styles.typingRow}>
          <View style={styles.typingDots}>
            <View style={styles.typingDot} />
            <View style={styles.typingDot} />
            <View style={styles.typingDot} />
          </View>
          <Text style={styles.typingText}>
            {Object.values(typingUsers).length === 1
              ? t('agreementDetail.messagesTypingOne', { name: Object.values(typingUsers)[0] })
              : t('agreementDetail.messagesTypingMany', { names: Object.values(typingUsers).join(', ') })}
          </Text>
        </View>
      )}

      <ErrorText>{error}</ErrorText>

      {needsSignature ? (
        <View style={[styles.closedNotice, { paddingBottom: insets.bottom + spacing.sm }]}>
          <Ionicons name="create-outline" size={16} color={colors.textMuted} />
          <Text style={styles.closedNoticeText}>{t('agreementDetail.messagesNeedsSignature')}</Text>
        </View>
      ) : isDisputed ? (
        <View style={[styles.closedNotice, { paddingBottom: insets.bottom + spacing.sm }]}>
          <Ionicons name="lock-closed-outline" size={16} color={colors.textMuted} />
          <Text style={styles.closedNoticeText}>{t('agreementDetail.messagesDisputeLocked')}</Text>
        </View>
      ) : isClosed ? (
        <View style={[styles.closedNotice, { paddingBottom: insets.bottom + spacing.sm }]}>
          <Ionicons name="lock-closed-outline" size={16} color={colors.textMuted} />
          <Text style={styles.closedNoticeText}>{t('agreementDetail.messagesClosedAgreement')}</Text>
        </View>
      ) : isBlocked ? (
        <View style={[styles.closedNotice, { paddingBottom: insets.bottom + spacing.sm, flexDirection: 'column', gap: spacing.xs }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name="ban-outline" size={16} color={colors.textMuted} />
            <Text style={styles.closedNoticeText}>
              {blockedByMeId
                ? t('agreementDetail.blockedByMeNotice', { name: nameFor(blockedByMeId) })
                : t('agreementDetail.blockedMeNotice', { name: nameFor(blockedMeId!) })}
            </Text>
          </View>
          {!!blockedByMeId && (
            <Pressable onPress={onUnblock} disabled={blockBusy} accessibilityRole="button">
              <Text style={styles.unblockLink}>
                {blockBusy ? t('common.loading') : t('agreementDetail.unblockAction')}
              </Text>
            </Pressable>
          )}
        </View>
      ) : (
        <View>
          {!!replyTo && (
            <View style={styles.replyPreviewBar}>
              <Ionicons name="arrow-undo" size={16} color={CHAT_AQUA} />
              <View style={{ flex: 1 }}>
                <Text style={styles.replyPreviewSender} numberOfLines={1}>{replyPreviewLabel(replyTo)}</Text>
                <Text style={styles.replyPreviewText} numberOfLines={1}>{replyTo.body}</Text>
              </View>
              <Pressable onPress={() => setReplyTo(null)} hitSlop={10} accessibilityRole="button">
                <Ionicons name="close" size={18} color={colors.textMuted} />
              </Pressable>
            </View>
          )}
          <View style={[styles.inputRow, { paddingBottom: insets.bottom + spacing.sm }]}>
          <TextInput
            value={draft}
            onChangeText={onChangeDraft}
            placeholder={t('agreementDetail.messagesPlaceholder')}
            placeholderTextColor={colors.textMuted}
            style={styles.input}
            multiline
            maxLength={4000}
          />
          <Pressable
            style={[styles.sendBtn, (!draft.trim() || sending) && styles.sendBtnDisabled]}
            onPress={onSend}
            disabled={!draft.trim() || sending}
            accessibilityRole="button"
          >
            <Ionicons name="send" size={18} color="#fff" />
          </Pressable>
          </View>
        </View>
      )}

      {/* Action sheet σε long-press μηνύματος */}
      <Modal visible={!!actionSheetFor} transparent animationType="fade" onRequestClose={() => setActionSheetFor(null)}>
        <Pressable style={styles.overlay} onPress={() => setActionSheetFor(null)} accessibilityRole="button">
          <Pressable style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]} onPress={() => {}}>
            <View style={styles.sheetHandle} />
            {actionSheetFor && (
              <>
                <View style={styles.emojiPickerRow}>
                  {MESSAGE_REACTION_EMOJIS.map((emoji) => {
                    const selected = reactionsByMessage.mine[actionSheetFor.id]?.emoji === emoji;
                    return (
                      <Pressable
                        key={emoji}
                        style={[styles.emojiPickerBtn, selected && styles.emojiPickerBtnSelected]}
                        onPress={() => onPickReaction(actionSheetFor.id, emoji)}
                        hitSlop={6}
                        accessibilityRole="button"
                      >
                        <Text style={styles.emojiPickerEmoji}>{emoji}</Text>
                      </Pressable>
                    );
                  })}
                </View>
                <Pressable
                  style={styles.sheetRow}
                  onPress={() => { onSwipeReply(actionSheetFor); setActionSheetFor(null); }}
                  accessibilityRole="button"
                >
                  <Ionicons name="arrow-undo-outline" size={20} color={colors.text} />
                  <Text style={styles.sheetRowText}>{t('agreementDetail.replyAction')}</Text>
                </Pressable>
                {actionSheetFor.sender_id !== user?.id && (
                  <>
                    <Pressable style={styles.sheetRow} onPress={onOpenReportFromSheet} accessibilityRole="button">
                      <Ionicons name="flag-outline" size={20} color={colors.warning} />
                      <Text style={styles.sheetRowText}>{t('agreementDetail.reportMessageAction')}</Text>
                    </Pressable>
                    <Pressable style={styles.sheetRow} onPress={onBlockFromSheet} accessibilityRole="button">
                      <Ionicons name="ban-outline" size={20} color={colors.danger} />
                      <Text style={[styles.sheetRowText, { color: colors.danger }]}>
                        {t('agreementDetail.blockUserAction', { name: nameFor(actionSheetFor.sender_id) })}
                      </Text>
                    </Pressable>
                  </>
                )}
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Modal αναφοράς μηνύματος */}
      <Modal visible={!!reportTarget} transparent animationType="fade" onRequestClose={() => setReportTarget(null)}>
        <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
            <View style={styles.overlay}>
              <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]}>
                <View style={styles.sheetHandle} />
                <Text style={styles.sheetTitle}>{t('agreementDetail.reportModalTitle')}</Text>
                <View style={styles.reasonGrid}>
                  {REPORT_REASONS.map((r) => (
                    <Pressable
                      key={r}
                      style={[styles.reasonChip, reportReason === r && styles.reasonChipSelected]}
                      onPress={() => setReportReason(r)}
                      accessibilityRole="button"
                    >
                      <Text style={[styles.reasonChipText, reportReason === r && styles.reasonChipTextSelected]}>
                        {t(`agreementDetail.reportReason_${r}`)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
                <TextInput
                  value={reportNote}
                  onChangeText={setReportNote}
                  placeholder={t('agreementDetail.reportNotePlaceholder')}
                  placeholderTextColor={colors.textMuted}
                  style={styles.reportNoteInput}
                  multiline
                  numberOfLines={3}
                  maxLength={500}
                />
                <View style={styles.sheetBtnRow}>
                  <Pressable style={styles.sheetSecondaryBtn} onPress={() => setReportTarget(null)} accessibilityRole="button">
                    <Text style={styles.sheetSecondaryBtnText}>{t('common.cancel')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.sheetPrimaryBtn, reporting && styles.sendBtnDisabled]}
                    onPress={onSubmitReport}
                    disabled={reporting}
                    accessibilityRole="button"
                  >
                    {reporting
                      ? <ActivityIndicator color="#fff" size="small" />
                      : <Text style={styles.sheetPrimaryBtnText}>{t('agreementDetail.reportSubmit')}</Text>
                    }
                  </Pressable>
                </View>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: { flex: 1, textAlign: 'center', color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    participantsLine: {
      color: colors.textMuted,
      fontSize: 11,
      textAlign: 'center',
      paddingHorizontal: spacing.md,
      paddingBottom: spacing.xs,
    },
    noticeBanner: {
      gap: 6,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.sm,
      backgroundColor: colors.surfaceAlt,
    },
    noticeIconRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 6,
    },
    noticeText: { flex: 1, color: colors.textMuted, fontSize: 11, lineHeight: 14 },
    noticeDismissBtn: { alignSelf: 'flex-start' },
    noticeDismissText: { color: CHAT_AQUA, fontSize: 12, fontWeight: '700' },
    searchBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.xs,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surfaceAlt,
    },
    searchInput: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      paddingVertical: 4,
    },
    searchCount: { color: colors.textMuted, fontSize: 12 },
    bubbleActiveMatch: { borderWidth: 2, borderColor: '#FBBF24' },
    unreadDivider: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
    },
    unreadDividerLine: { flex: 1, height: 1, backgroundColor: CHAT_AQUA, opacity: 0.4 },
    unreadDividerText: { color: CHAT_AQUA, fontSize: 11, fontWeight: '700', letterSpacing: 0.3 },
    bubbleHighlight: { backgroundColor: '#FBBF24', color: '#1a1a1a', borderRadius: 2 },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingTop: spacing.xl * 2 },
    emptyText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center', paddingHorizontal: spacing.xl },
    listContent: {
      // Σε πλατιά οθόνη τα μηνύματα μένουν σε στήλη αναγνώσιμου πλάτους
      // αντί να απλώνονται από άκρη σε άκρη.
      width: '100%',
      maxWidth: contentMaxWidth.wide,
      alignSelf: 'center',
      padding: spacing.md,
      gap: spacing.xs,
      flexGrow: 1,
    },
    bubbleRow: { flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: 2 },
    bubbleRowMine: { justifyContent: 'flex-end' },
    bubbleMenuBtn: { padding: 4, alignSelf: 'flex-end' },
    bubbleColumn: { maxWidth: '80%' },
    bubble: {
      borderRadius: radius.lg,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      marginVertical: 2,
    },
    reactionsBadge: {
      flexDirection: 'row',
      alignSelf: 'flex-start',
      gap: 2,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingHorizontal: 6,
      paddingVertical: 2,
      marginTop: -8,
      marginLeft: spacing.sm,
    },
    reactionsBadgeMine: { alignSelf: 'flex-end', marginRight: spacing.sm, marginLeft: 0 },
    reactionsBadgeText: { fontSize: 12 },
    bubbleOther: { backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border },
    bubbleMine: { backgroundColor: CHAT_AQUA },
    bubbleSender: { color: CHAT_AQUA, fontSize: 11, fontWeight: '700', marginBottom: 2 },
    bubbleSenderMine: { color: 'rgba(255,255,255,0.85)' },
    bubbleBusinessLine: { color: colors.textMuted, fontSize: 10, marginTop: -2, marginBottom: 2 },
    bubbleText: { color: colors.text, fontSize: fontSize.sm, lineHeight: 19 },
    bubbleTextMine: { color: '#fff' },
    bubbleTime: { color: colors.textMuted, fontSize: 10, marginTop: 2, alignSelf: 'flex-end' },
    bubbleTimeMine: { color: 'rgba(255,255,255,0.75)' },
    bubbleMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 3, alignSelf: 'flex-end', marginTop: 2 },
    bubbleReadIcon: { marginLeft: 1 },
    bubbleReadEmoji: { fontSize: 11, marginLeft: 1 },
    bubbleReadByText: { color: 'rgba(255,255,255,0.6)', fontSize: 9.5, alignSelf: 'flex-end', marginTop: 1 },
    typingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.xs,
    },
    typingDots: { flexDirection: 'row', gap: 3 },
    typingDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: colors.textMuted },
    typingText: { color: colors.textMuted, fontSize: 12, fontStyle: 'italic' },
    error: { color: colors.danger, fontSize: fontSize.sm, textAlign: 'center', paddingVertical: 4 },
    closedNotice: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    closedNoticeText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center' },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    input: {
      flex: 1,
      maxHeight: 100,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      color: colors.text,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      fontSize: fontSize.sm,
    },
    sendBtn: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: CHAT_AQUA,
      alignItems: 'center',
      justifyContent: 'center',
    },
    sendBtnDisabled: { opacity: 0.5 },
    swipeReplyIcon: {
      width: 48,
      alignItems: 'center',
      justifyContent: 'center',
    },
    quoteBox: {
      borderLeftWidth: 3,
      borderLeftColor: CHAT_AQUA,
      backgroundColor: 'rgba(255,255,255,0.06)',
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: spacing.sm,
      marginBottom: 4,
    },
    quoteBoxMine: {
      borderLeftColor: 'rgba(255,255,255,0.8)',
      backgroundColor: 'rgba(0,0,0,0.12)',
    },
    quoteSender: { color: CHAT_AQUA, fontSize: 11, fontWeight: '700' },
    quoteSenderMine: { color: 'rgba(255,255,255,0.9)' },
    quoteText: { color: colors.textMuted, fontSize: 12 },
    quoteTextMine: { color: 'rgba(255,255,255,0.75)' },
    replyPreviewBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      backgroundColor: colors.surfaceAlt,
    },
    replyPreviewSender: { color: CHAT_AQUA, fontSize: 12, fontWeight: '700' },
    replyPreviewText: { color: colors.textMuted, fontSize: 12 },
    unblockLink: { color: CHAT_AQUA, fontSize: fontSize.sm, fontWeight: '700' },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.md,
    },
    sheetHandle: {
      width: 40, height: 4, borderRadius: 2,
      backgroundColor: colors.border, alignSelf: 'center',
    },
    emojiPickerRow: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      marginBottom: spacing.xs,
    },
    emojiPickerBtn: {
      width: 44, height: 44, borderRadius: 22,
      alignItems: 'center', justifyContent: 'center',
    },
    emojiPickerBtnSelected: { backgroundColor: colors.surfaceAlt },
    emojiPickerEmoji: { fontSize: 26 },
    sheetRow: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
      paddingVertical: spacing.sm,
    },
    sheetRowText: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    sheetTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700', textAlign: 'center' },
    reasonGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    reasonChip: {
      borderRadius: radius.full, borderWidth: 1.5, borderColor: colors.border,
      paddingVertical: 7, paddingHorizontal: spacing.md, backgroundColor: colors.surfaceAlt,
    },
    reasonChipSelected: { backgroundColor: CHAT_AQUA, borderColor: CHAT_AQUA },
    reasonChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    reasonChipTextSelected: { color: colors.textOnPrimary },
    reportNoteInput: {
      borderWidth: 1, borderRadius: radius.md, borderColor: colors.border,
      backgroundColor: colors.surfaceAlt, color: colors.text,
      padding: spacing.md, minHeight: 80, fontSize: fontSize.sm, textAlignVertical: 'top',
    },
    sheetBtnRow: { flexDirection: 'row', gap: spacing.sm },
    sheetSecondaryBtn: {
      flex: 1, height: 46, borderRadius: radius.md, borderWidth: 1.5, borderColor: colors.border,
      alignItems: 'center', justifyContent: 'center',
    },
    sheetSecondaryBtnText: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '600' },
    sheetPrimaryBtn: {
      flex: 1, height: 46, borderRadius: radius.md, backgroundColor: CHAT_AQUA,
      alignItems: 'center', justifyContent: 'center',
    },
    sheetPrimaryBtnText: { color: '#fff', fontSize: fontSize.md, fontWeight: '700' },
  });
}
